import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { X, Stethoscope, LayoutGrid, TrendingUp, ArrowRight } from "lucide-react";

interface CommunityHospitalsModalProps {
  open: boolean;
  onClose: () => void;
}

const cards = [
  {
    featured: true,
    icon: Stethoscope,
    title: "Curbside & eConsult",
    body: "Same-day specialist input for your ED and inpatient teams — no transfer needed.",
    cta: "See how it works",
    href: "/hospital-curbside-econsult",
    chips: ["INPATIENT", "OUTPATIENT", "UC"],
  },
  {
    featured: false,
    icon: LayoutGrid,
    title: "Tele clinical services",
    body: "Tele-hospitalist, tele-specialists, RT, case management, and UM. Add what you need.",
    cta: "See all services",
    href: "/inpatient-clinical-services",
    chips: ["Inpatient", "Outpatient", "ER"],
  },
  {
    featured: false,
    icon: TrendingUp,
    title: "Inpatient revenue capture",
    body: "Fix leakage, status, DRG, and denials. One integrated team, real accountability.",
    cta: "See the framework",
    href: "/inpatient-revenue-framework",
    chips: ["Status", "CDI", "Denials"],
  },
];

export function CommunityHospitalsModal({ open, onClose }: CommunityHospitalsModalProps) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-foreground/60 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal */}
          <motion.div
            className="relative w-full max-w-4xl bg-background rounded-2xl shadow-2xl border border-border/30 p-6 md:p-10 max-h-[92vh] overflow-y-auto"
            initial={{ opacity: 0, y: 32, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors p-1"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <p className="text-[11px] font-heading font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2">
              Community Hospitals
            </p>
            <h2 className="font-heading font-extrabold text-2xl md:text-3xl tracking-tight text-foreground mb-8">
              What are you looking for?
            </h2>

            {/* Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {cards.map((card) => (
                <Link
                  key={card.href}
                  to={card.href}
                  onClick={onClose}
                  className={`group flex flex-col rounded-xl border p-6 transition-all duration-300 hover:shadow-lg ${
                    card.featured
                      ? "border-primary/40 bg-primary/[0.04] hover:border-primary/60"
                      : "border-border/40 bg-background hover:border-border/70"
                  }`}
                >
                  <card.icon
                    className={`w-7 h-7 mb-4 ${
                      card.featured ? "text-primary" : "text-muted-foreground"
                    }`}
                    strokeWidth={1.8}
                  />
                  <h3 className="font-heading font-bold text-[1.02rem] leading-snug text-foreground mb-2">
                    {card.title}
                  </h3>
                  <p className="text-muted-foreground text-[13.5px] leading-relaxed mb-4 flex-1">
                    {card.body}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {card.chips.map((chip) => (
                      <span
                        key={chip}
                        className="text-[10.5px] font-medium tracking-wide uppercase text-muted-foreground/80 bg-muted/60 px-2 py-0.5 rounded"
                      >
                        {chip}
                      </span>
                    ))}
                  </div>
                  <span
                    className={`flex items-center gap-1.5 text-[13px] font-medium ${
                      card.featured
                        ? "text-primary group-hover:text-primary/80"
                        : "text-muted-foreground group-hover:text-foreground"
                    } transition-colors`}
                  >
                    {card.cta}
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </Link>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
